import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useUser } from '../../contexts/UserContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useApi } from '../../hooks/useApi';
import { hapticFeedback } from '../../utils/telegram';
import './Arbitrage.css';
import FormattedNumber from '../common/FormattedNumber/FormattedNumber';
import SwipeToGetFoxButton from '../common/SwipeToGetFoxButton/SwipeToGetFoxButton';
import { useConfig } from '../../hooks/useConfig';

const TRADE_STAGES = {
  SELECT_MODE: 'SELECT_MODE',
  SELECT_OPPORTUNITY: 'SELECT_OPPORTUNITY',
  AUTOMATIC_SETUP: 'AUTOMATIC_SETUP',
  ENTER_AMOUNT: 'ENTER_AMOUNT',
  EXECUTION: 'EXECUTION',
  COMPLETED: 'COMPLETED'
};

const REFRESH_COOLDOWN = 30; // Cooldown in seconds

const Arbitrage = () => {
  const { user, refreshUserData, activeTrade, setActiveManualTrade, clearActiveTrade } = useUser();
  const { t } = useLanguage();
  const {
    getArbitrageOpportunities,
    executeArbitrage,
    getArbitrageHistory,
    // Auto-trading methods
    getAutoTradeSettings,
    updateAutoTradeSettings,
    startAutoTrade,
    stopAutoTrade,
    getAutoTradeStatus,
    getUserData
  } = useApi();

  const [tradeStage, setTradeStage] = useState(TRADE_STAGES.SELECT_MODE);
  const [tradeMode, setTradeMode] = useState('null'); // 'manual' or 'automatic'
  const [opportunities, setOpportunities] = useState([]);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [amount, setAmount] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [executionStep, setExecutionStep] = useState(0);
  const [executionProgress, setExecutionProgress] = useState(0);
  const [tradeResult, setTradeResult] = useState(null);
  const config = useConfig();

  // Шаги выполнения арбитражной сделки
  const executionSteps = [
    { text: t('connectingToExchange'), exchange: 'buyExchange' },
    { text: t('checkingLiquidity'), exchange: 'buyExchange' },
    { text: t('buyingCoin'), exchange: 'buyExchange' },
    { text: t('transferringCoin'), exchange: null },
    { text: t('connectingToExchange'), exchange: 'sellExchange' },
    { text: t('sellingCoins'), exchange: 'sellExchange' },
    { text: t('fixingProfit'), exchange: null }
  ];

  // Функция для симуляции прогресса выполнения сделки
  const simulateTradeExecution = useCallback(() => {
    const stepDurations = [2000, 1500, 3000, 2500, 2000, 3000, 1500]; // Длительность каждого шага в мс

    const executeStep = (stepIndex) => {
      if (stepIndex >= executionSteps.length) {
        // Завершение выполнения
        setExecutionProgress(100);
        setIsLoading(false);
        return;
      }

      setExecutionStep(stepIndex);

      // Сохраняем прогресс в активной сделке
      setActiveManualTrade(prev => ({
        ...prev,
        executionStep: stepIndex
      }));

      // Анимация прогресса внутри шага
      const stepProgress = (stepIndex / executionSteps.length) * 100;
      const nextStepProgress = ((stepIndex + 1) / executionSteps.length) * 100;

      let progressIncrement = 0;
      const progressInterval = setInterval(() => {
        progressIncrement += 2;
        const currentStepProgress = stepProgress + (progressIncrement / 100) * (nextStepProgress - stepProgress);
        setExecutionProgress(Math.min(currentStepProgress, nextStepProgress));

        // Сохраняем прогресс в активной сделке
        setActiveManualTrade(prev => ({
          ...prev,
          executionProgress: Math.min(currentStepProgress, nextStepProgress)
        }));

        if (progressIncrement >= 100) {
          clearInterval(progressInterval);
          setTimeout(() => executeStep(stepIndex + 1), 200);
        }
      }, stepDurations[stepIndex] / 50);
    };

    executeStep(0);
  }, [executionSteps, setActiveManualTrade]);

  // Функция для продолжения выполнения сделки с определенного шага
  const continueTradeExecution = useCallback((fromStep, fromProgress) => {
    const stepDurations = [2000, 1500, 3000, 2500, 2000, 3000, 1500]; // Длительность каждого шага в мс

    // Проверяем статус сделки через API
    const checkTradeStatus = async () => {
      try {
        console.log('🔍 Checking trade status...');

        // Проверяем, не прошло ли слишком много времени с начала сделки
        const currentTime = Date.now();
        const startTime = activeTrade?.startTime || currentTime;
        const elapsedTime = currentTime - startTime;

        // Если прошло больше 20 секунд, считаем что сделка должна быть завершена
        if (elapsedTime > 20000) {
          console.log('🕐 Trade execution time exceeded, checking for completion...');

          // Здесь можно добавить реальную проверку статуса через API
          // const tradeStatus = await checkArbitrageStatus(activeTrade.tradeId);
          // if (tradeStatus.completed) {
          //   setTradeResult(tradeStatus.result);
          //   setTradeStage(TRADE_STAGES.COMPLETED);
          //   setIsLoading(false);
          //   return;
          // }

          // Пока что просто завершаем симуляцию
          setExecutionProgress(100);
          setIsLoading(false);
          console.log('⚠️ Trade execution completed by timeout');
          return;
        }

        // Продолжаем симуляцию
        executeStepFromCurrent(fromStep, fromProgress);
      } catch (error) {
        console.error('Error checking trade status:', error);
        // В случае ошибки продолжаем симуляцию
        executeStepFromCurrent(fromStep, fromProgress);
      }
    };

    const executeStepFromCurrent = (stepIndex, currentProgress) => {
      if (stepIndex >= executionSteps.length) {
        // Завершение выполнения
        setExecutionProgress(100);
        setIsLoading(false);
        return;
      }

      setExecutionStep(stepIndex);

      // Анимация прогресса внутри шага
      const stepProgress = (stepIndex / executionSteps.length) * 100;
      const nextStepProgress = ((stepIndex + 1) / executionSteps.length) * 100;

      // Определяем, с какого прогресса внутри шага начинать
      const stepProgressRange = nextStepProgress - stepProgress;
      const currentStepProgress = currentProgress - stepProgress;
      const remainingStepProgress = Math.max(0, stepProgressRange - currentStepProgress);

      let progressIncrement = (currentStepProgress / stepProgressRange) * 100;

      const progressInterval = setInterval(() => {
        progressIncrement += 2;
        const newStepProgress = stepProgress + (progressIncrement / 100) * stepProgressRange;
        setExecutionProgress(Math.min(newStepProgress, nextStepProgress));

        // Сохраняем прогресс в активной сделке
        setActiveManualTrade(prev => ({
          ...prev,
          executionProgress: Math.min(newStepProgress, nextStepProgress)
        }));

        if (progressIncrement >= 100) {
          clearInterval(progressInterval);
          setTimeout(() => executeStepFromCurrent(stepIndex + 1, nextStepProgress), 200);
        }
      }, stepDurations[stepIndex] / 50);
    };

    // Запускаем проверку статуса
    checkTradeStatus();
  }, [executionSteps, setActiveManualTrade]);

  // User data loading state
  const [isLoadingUserData, setIsLoadingUserData] = useState(false);

  // Refresh cooldown state
  const [refreshCooldown, setRefreshCooldown] = useState(0);
  const cooldownTimer = useRef(null);

  // Status refresh cooldown state
  const [statusRefreshCooldown, setStatusRefreshCooldown] = useState(0);
  const statusCooldownTimer = useRef(null);

  // Automatic trade settings
  const [selectedPair, setSelectedPair] = useState('BTCUSDT');
  const [tradeCount, setTradeCount] = useState('');
  const [minSpread, setMinSpread] = useState('');
  const [autoAmount, setAutoAmount] = useState('');

  // Auto-trading state
  const [selectedPairs, setSelectedPairs] = useState([]); // Multiple pairs
  const [maxSpread, setMaxSpread] = useState('');
  const [autoTradeStatus, setAutoTradeStatus] = useState(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);

  // Флаг для предотвращения дублирования запросов
  const [isInitialized, setIsInitialized] = useState(false);

  // Состояние для модального окна Fox
  const [showFoxModal, setShowFoxModal] = useState(false);

  const balance = user?.balance || 0;
  const availableTrades = user?.availableDeals || 0;
  const maxPerDeal = user?.lincenseData?.maxDealAmount || 0;
  const minPerDeal = user?.lincenseData?.minDealAmount || 10;

  // Function to refresh user data
  const handleRefreshUserData = useCallback(async () => {
    setIsLoadingUserData(true);
    try {
      // Используем refreshUserData из контекста, который теперь не изменяет loading состояние
      await refreshUserData();

      console.log('✅ User data refreshed successfully');
    } catch (error) {
      console.error('❌ Error refreshing user data:', error);
      setError(t('errorRefreshingUserData'));
    } finally {
      setIsLoadingUserData(false);
    }
  }, [refreshUserData, t]);

  const refreshOpportunities = useCallback(async (isReload = false) => {
    console.log('refreshOpportunities called with isReload:', isReload);
    hapticFeedback.light();

    // Start cooldown timer
    setRefreshCooldown(REFRESH_COOLDOWN);

    setIsLoading(true);
    setError(null);

    try {
      const data = await getArbitrageOpportunities(isReload);

      // Transform the data to match the component's expected format
      const formattedOpportunities = data.map(opp => {
        // Ensure sourcePrice and targetPrice are numbers
        const sourcePrice = parseFloat(opp.sourcePrice) || 0;
        const targetPrice = parseFloat(opp.targetPrice) || 0;

        // Create a formatted prices object from all exchanges
        const prices = {};

        // Process all exchanges from the exchanges object
        if (opp.exchanges) {
          Object.entries(opp.exchanges).forEach(([exchange, data]) => {
            prices[exchange.toUpperCase()] = {
              price: parseFloat(data.price) || 0,
              isPrimary: exchange === opp.sourceExchange || exchange === opp.targetExchange
            };
          });
        }

        return {
          id: opp.id,
          pair: opp.pair,
          available: true,
          buyExchange: opp.sourceExchange.toUpperCase(),
          sellExchange: opp.targetExchange.toUpperCase(),
          buyPrice: sourcePrice,
          sellPrice: targetPrice,
          spread: parseFloat(opp.profitPercent) || 0,
          prices,
          allExchanges: opp.exchanges || {},
          timestamp: new Date(opp.timestamp).toLocaleTimeString(),
          date: new Date(opp.timestamp).toLocaleDateString(),
          canSwap: opp.canSwap !== undefined ? opp.canSwap : true // Default to true if not specified
        };
      });

      setOpportunities(formattedOpportunities);
    } catch (err) {
      console.error('Error fetching arbitrage opportunities:', err);
      setError(t('errorFetchingArbitrageOpportunities'));
    } finally {
      setIsLoading(false);
    }
  }, [getArbitrageOpportunities, t]);

  // Auto-trading functions
  const loadAutoTradeSettings = useCallback(async () => {
    try {
      const response = await getAutoTradeSettings();
      if (response.success && response.settings) {
        const settings = response.settings.settings;
        if (settings) {
          setSelectedPairs(['BTCUSDT']);
          setTradeCount(settings.countDeals || '');
          setMinSpread(settings.minSpread || '');
          setMaxSpread(settings.maxSpread || '');
          setAutoAmount(settings.totalToOneOrder || '');
        }
      }
    } catch (err) {
      console.error('Error loading auto-trade settings:', err);
    }
  }, [getAutoTradeSettings]);

  const loadAutoTradeStatus = useCallback(async () => {
    hapticFeedback.light();

    // Start cooldown timer
    setStatusRefreshCooldown(REFRESH_COOLDOWN);

    setIsLoadingStatus(true);
    try {
      const response = await getAutoTradeStatus();
      if (response.success) {
        setAutoTradeStatus(response.status);
      }
    } catch (err) {
      console.error('Error loading auto-trade status:', err);
    } finally {
      setIsLoadingStatus(false);
    }
  }, [getAutoTradeStatus]);

  // Fetch opportunities on component mount
  useEffect(() => {
    // Предотвращаем дублирование запросов в React.StrictMode
    if (isInitialized) return;

    // Загружаем данные при монтировании компонента
    const initializeData = async () => {
      console.log('🔄 Initializing Arbitrage page data...');

      // Проверяем активную сделку и восстанавливаем состояние только для важных этапов
      if (activeTrade) {
        console.log('🔄 Checking active trade:', activeTrade);
        
        // Восстанавливаем состояние только для сделок в процессе выполнения или завершенных
        const shouldRestoreTrade = activeTrade.stage === TRADE_STAGES.EXECUTION || 
                                  activeTrade.stage === TRADE_STAGES.COMPLETED;
        
        if (shouldRestoreTrade) {
          console.log('🔄 Restoring active/completed trade:', activeTrade);
          setTradeStage(activeTrade.stage);
          setTradeMode(activeTrade.mode);
          
          if (activeTrade.selectedOpportunity) {
            setSelectedOpportunity(activeTrade.selectedOpportunity);
          }
          if (activeTrade.amount) {
            setAmount(activeTrade.amount);
          }
          if (activeTrade.executionStep !== undefined) {
            setExecutionStep(activeTrade.executionStep);
          }
          if (activeTrade.executionProgress !== undefined) {
            setExecutionProgress(activeTrade.executionProgress);
          }
          if (activeTrade.tradeResult) {
            setTradeResult(activeTrade.tradeResult);
          }
          if (activeTrade.isLoading !== undefined) {
            setIsLoading(activeTrade.isLoading);
          }

          // Если сделка была в процессе выполнения, продолжаем симуляцию
          if (activeTrade.stage === TRADE_STAGES.EXECUTION && activeTrade.isLoading) {
            console.log('🔄 Continuing trade execution from step:', activeTrade.executionStep);
            // Продолжаем симуляцию с текущего шага
            continueTradeExecution(activeTrade.executionStep, activeTrade.executionProgress);
          }
        } else {
          // Для всех остальных этапов (выбор режима, возможностей, настройка автоторговли) - сбрасываем
          console.log('🔄 Clearing non-critical trade state and resetting to SELECT_MODE');
          clearActiveTrade();
          setTradeStage(TRADE_STAGES.SELECT_MODE);
          setTradeMode('null');
          setSelectedOpportunity(null);
          setAmount('');
          setError(null);
        }
      } else {
        // Если нет активной сделки, устанавливаем начальное состояние
        console.log('🔄 No active trade, setting initial state');
        setTradeStage(TRADE_STAGES.SELECT_MODE);
        setTradeMode('null');
        setSelectedOpportunity(null);
        setAmount('');
        setError(null);
      }

      // Refresh user data first
      await handleRefreshUserData();

      // Then load other data
      await Promise.all([
        refreshOpportunities(),
        loadAutoTradeSettings(),
        loadAutoTradeStatus()
      ]);
    };

    initializeData();
    setIsInitialized(true);

    // Clean up timer on unmount
    return () => {
      if (cooldownTimer.current) {
        clearInterval(cooldownTimer.current);
      }
      if (statusCooldownTimer.current) {
        clearInterval(statusCooldownTimer.current);
      }
    };
  }, []); // Убираем все зависимости, чтобы useEffect выполнился только один раз

  // Handle cooldown timer
  useEffect(() => {
    if (refreshCooldown > 0) {
      cooldownTimer.current = setInterval(() => {
        setRefreshCooldown(prev => {
          if (prev <= 1) {
            clearInterval(cooldownTimer.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (cooldownTimer.current) {
        clearInterval(cooldownTimer.current);
      }
    };
  }, [refreshCooldown]);

  // Handle status cooldown timer
  useEffect(() => {
    if (statusRefreshCooldown > 0) {
      statusCooldownTimer.current = setInterval(() => {
        setStatusRefreshCooldown(prev => {
          if (prev <= 1) {
            clearInterval(statusCooldownTimer.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (statusCooldownTimer.current) {
        clearInterval(statusCooldownTimer.current);
      }
    };
  }, [statusRefreshCooldown]);

  const handlePairToggle = useCallback((pair) => {
    hapticFeedback.selectionChanged();
    setError(null); // Clear error when user makes changes
    setSelectedPairs(prev => {
      const isSelected = prev.includes(pair);
      if (isSelected) {
        // Remove pair if already selected (but keep at least one)
        if (prev.length > 1) {
          return prev.filter(p => p !== pair);
        }
        return prev;
      } else {
        // Add pair if not selected
        return [...prev, pair];
      }
    });
  }, []);

  const handleAutoTradeSetup = useCallback(async (e) => {
    e.preventDefault();
    hapticFeedback.medium();

    // Check if user has enough balance for one deal
    const amountPerDeal = parseFloat(autoAmount);
    if (amountPerDeal > balance) {
      hapticFeedback.error();
      setError(t('insufficientBalanceForTrade'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Save settings and start auto-trade
      const settings = {
        coins: selectedPairs,
        countDeals: parseInt(tradeCount),
        minSpread: parseFloat(minSpread),
        maxSpread: parseFloat(maxSpread),
        totalToOneOrder: parseFloat(autoAmount)
      };

      // First save the settings
      await updateAutoTradeSettings(settings);

      // Then start auto-trading
      await startAutoTrade();

      // Refresh user data after starting auto-trade
      await handleRefreshUserData();

      hapticFeedback.success();
      loadAutoTradeStatus(); // Refresh status
    } catch (err) {
      console.error('Error setting up automatic trading:', err);
      hapticFeedback.error();

      // Better error message handling
      let errorMessage = t('failedToSetupAutoTrading');

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [autoAmount, tradeCount, balance, selectedPairs, minSpread, maxSpread, updateAutoTradeSettings, startAutoTrade, handleRefreshUserData, loadAutoTradeStatus, t]);

  const handleStopAutoTrade = useCallback(async () => {
    hapticFeedback.medium();
    setIsLoading(true);

    try {
      await stopAutoTrade();

      // Refresh user data after stopping auto-trade
      await handleRefreshUserData();

      hapticFeedback.success();
      loadAutoTradeStatus(); // Refresh status
    } catch (err) {
      console.error('Error stopping auto-trade:', err);
      hapticFeedback.error();
    } finally {
      setIsLoading(false);
    }
  }, [stopAutoTrade, handleRefreshUserData, loadAutoTradeStatus]);

  const handleRestartAutoTrade = useCallback(async () => {
    hapticFeedback.medium();
    setIsLoading(true);
    setError(null);

    try {
      // Start auto-trading with existing settings
      await startAutoTrade();
      hapticFeedback.success();
      loadAutoTradeStatus(); // Refresh status
    } catch (err) {
      console.error('Error restarting auto-trade:', err);
      hapticFeedback.error();

      // Better error message handling
      let errorMessage = t('failedToRestartAutoTrading');

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [startAutoTrade, loadAutoTradeStatus, t]);

  const handleNewSession = useCallback(async () => {
    hapticFeedback.medium();

    // Просто сбрасываем локальный статус чтобы показать форму настройки
    setAutoTradeStatus(null);
    setError(null);
  }, []);

  const handleModeSelect = useCallback((mode) => {
    hapticFeedback.light();
    setTradeMode(mode);
    setError(null); // Clear any previous errors when switching modes

    // Проверяем, есть ли активная ручная сделка
    if (mode === 'manual' && activeTrade && activeTrade.mode === 'manual') {
      // Если есть активная ручная сделка, восстанавливаем её состояние
      console.log('🔄 Continuing active manual trade');
      return; // Не меняем stage, оставляем текущее состояние
    }

    if (mode === 'manual') {
      setTradeStage(TRADE_STAGES.SELECT_OPPORTUNITY);
      refreshOpportunities(); // Refresh opportunities when selecting manual mode
    } else {
      setTradeStage(TRADE_STAGES.AUTOMATIC_SETUP);
      // Не загружаем статус автоматически - пользователь может хотеть создать новую сессию
    }
  }, [refreshOpportunities, activeTrade]);

  const handleOpportunitySelect = useCallback((opportunity) => {
    hapticFeedback.medium();
    setSelectedOpportunity(opportunity);
    setTradeStage(TRADE_STAGES.ENTER_AMOUNT);

    // Сохраняем активную сделку
    setActiveManualTrade({
      stage: TRADE_STAGES.ENTER_AMOUNT,
      mode: 'manual',
      selectedOpportunity: opportunity,
      amount: '',
      isLoading: false
    });
  }, [setActiveManualTrade]);

  const handleAmountChange = useCallback((e) => {
    const newAmount = e.target.value;
    setAmount(newAmount);

    // Обновляем активную сделку с новой суммой
    if (activeTrade) {
      setActiveManualTrade({
        ...activeTrade,
        amount: newAmount
      });
    }
  }, [activeTrade, setActiveManualTrade]);

  const handleNext = useCallback(async () => {
    hapticFeedback.medium();

    // Check if user has enough balance
    if (parseFloat(amount) > balance) {
      hapticFeedback.error();
      setError(t('insufficientBalanceForTradeExecution'));
      return;
    }

    // Check minimum deal amount
    if (parseFloat(amount) < minPerDeal) {
      hapticFeedback.error();
      setError(t('minimumDealAmount') + ` $${minPerDeal}`);
      return;
    }

    // Check maximum deal amount
    if (maxPerDeal > 0 && parseFloat(amount) > maxPerDeal) {
      hapticFeedback.error();
      setError(t('maximumDealAmount') + ` $${maxPerDeal}`);
      return;
    }

    setTradeStage(TRADE_STAGES.EXECUTION);
    setIsLoading(true);
    setError(null);
    setExecutionStep(0);
    setExecutionProgress(0);

    // Сохраняем состояние выполнения сделки
    setActiveManualTrade({
      stage: TRADE_STAGES.EXECUTION,
      mode: 'manual',
      selectedOpportunity,
      amount,
      executionStep: 0,
      executionProgress: 0,
      isLoading: true,
      startTime: Date.now() // Добавляем время начала для отслеживания
    });

    // Запускаем симуляцию выполнения сделки
    simulateTradeExecution();

    // Выполняем API вызов параллельно с симуляцией
    try {
      // Prepare operation data
      const operationData = {
        pair: selectedOpportunity.pair,
        sourceExchange: selectedOpportunity.buyExchange,
        targetExchange: selectedOpportunity.sellExchange,
        amount: parseFloat(amount),
        profitPercent: selectedOpportunity.spread
      };

      // Call the API to execute the arbitrage
      const result = await executeArbitrage(operationData);

      // Refresh user data after successful trade
      await handleRefreshUserData();

      // Сразу показываем результат после получения ответа от API
      const tradeResultData = {
        pair: result.pair || selectedOpportunity.pair,
        amount: parseFloat(amount),
        volume: result.volume || (parseFloat(amount) / selectedOpportunity.buyPrice),
        buyExchange: result.sourceExchange || selectedOpportunity.buyExchange,
        sellExchange: result.targetExchange || selectedOpportunity.sellExchange,
        spread: result.profitPercent || selectedOpportunity.spread,
        profit: result.profit || (parseFloat(amount) * (selectedOpportunity.spread / 100)),
        commission: result.commission || parseFloat(((parseFloat(amount) * (selectedOpportunity.spread / 100)) * 0.4).toFixed(2)),
        commissionAmount: result.commissionAmount || 0,
        newBalance: (user?.balance || 0) + (result.profit || (parseFloat(amount) * (selectedOpportunity.spread / 100))),
        ...result
      };

      setTradeResult(tradeResultData);
      setTradeStage(TRADE_STAGES.COMPLETED);
      setIsLoading(false);

      // Сохраняем завершенную сделку
      setActiveManualTrade({
        stage: TRADE_STAGES.COMPLETED,
        mode: 'manual',
        selectedOpportunity,
        amount,
        tradeResult: tradeResultData,
        isLoading: false,
        completedTime: Date.now()
      });

    } catch (err) {
      console.error('Error executing arbitrage:', err);

      // Немедленно показываем ошибку, останавливаем симуляцию
      setIsLoading(false);
      setExecutionProgress(0);
      setExecutionStep(0);

      // Определяем сообщение об ошибке
      let errorMessage = t('failedToExecuteArbitrage');

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setTradeStage(TRADE_STAGES.ENTER_AMOUNT); // Go back to the form

      // Обновляем активную сделку при ошибке
      setActiveManualTrade({
        stage: TRADE_STAGES.ENTER_AMOUNT,
        mode: 'manual',
        selectedOpportunity,
        amount,
        executionStep: 0,
        executionProgress: 0,
        isLoading: false
      });

      hapticFeedback.error();
    }
  }, [amount, balance, selectedOpportunity, executeArbitrage, handleRefreshUserData, user?.balance, minPerDeal, maxPerDeal, t]);

  const handleDone = useCallback(() => {
    hapticFeedback.success();
    setTradeStage(TRADE_STAGES.SELECT_MODE);
    setSelectedOpportunity(null);
    setAmount('');
    setTradeResult(null);
    setError(null);

    // Очищаем активную сделку
    clearActiveTrade();
  }, [clearActiveTrade]);

  const handleGetFoxTokens = useCallback(() => {
    hapticFeedback.medium();
    // Здесь будет логика получения Fox токенов
    console.log('Getting 10 Fox tokens...');

    // Закрываем модальное окно
    setShowFoxModal(false);

    // Обновляем возможности арбитража
    refreshOpportunities(true);
  }, [refreshOpportunities]);

  // Add error handling to the UI
  const renderError = useCallback(() => {
    if (!error) return null;

    return (
      <div className="error-message">
        {error}
      </div>
    );
  }, [error]);

  const renderBalanceCard = useCallback(() => (
    <div className="balance-card">
      <div className="balance-header">
        <div className="balance-title">{t('balanceAndLimitsTitle')}</div>
        <button
          className="refresh-user-data-button"
          onClick={handleRefreshUserData}
          disabled={isLoadingUserData}
          title={t('update')}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={isLoadingUserData ? 'spinning' : ''}
          >
            <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4C7.58 4 4.01 7.58 4.01 12C4.01 16.42 7.58 20 12 20C15.73 20 18.84 17.45 19.73 14H17.65C16.83 16.33 14.61 18 12 18C8.69 18 6 15.31 6 12C6 8.69 8.69 6 12 6C13.66 6 15.14 6.69 16.22 7.78L13 11H20V4L17.65 6.35Z" fill="currentColor" />
          </svg>
        </button>
      </div>
      <div className="balance-row">
        <div className="balance-label">{t('totalBalance')}</div>
        {isLoadingUserData ? (
          <div className="balance-value skeleton-text">{t('loading')}</div>
        ) : (
          <FormattedNumber value={balance} suffix="USDT" className="balance-value" />
        )}
      </div>
      <div className="balance-row">
        <div className="balance-label">{t('availableTradesLabel')}</div>
        {isLoadingUserData ? (
          <div className="balance-value skeleton-text">{t('loading')}</div>
        ) : (
          <div className="balance-value">{availableTrades}</div>
        )}
      </div>
    </div>
  ), [handleRefreshUserData, isLoadingUserData, balance, availableTrades, t]);

  const renderModeSelection = useCallback(() => (
    <>
      {renderBalanceCard()}

      <h2 className="section-title">{t('chooseTradeModeTitle')}</h2>

      <div className="trade-mode-buttons">
        <button
          className={`mode-button ${tradeMode === 'manual' ? 'active' : ''}`}
          onClick={() => handleModeSelect('manual')}
        >
          {t('manualMode')}
        </button>
        <button
          className={`mode-button ${tradeMode === 'automatic' ? 'active' : ''}`}
          onClick={() => handleModeSelect('automatic')}
        >
          {t('automaticMode')}
        </button>
      </div>
    </>
  ), [renderBalanceCard, tradeMode, handleModeSelect, t]);

  const renderRefreshSection = useCallback(() => (
    <div className="refresh-section">
      <div className="refresh-label">
        {t('refreshTradesButton')}
        {refreshCooldown > 0 && (
          <span className="refresh-cooldown"> ({refreshCooldown}с)</span>
        )}
      </div>
      <button
        className="refresh-button"
        onClick={() => {
          hapticFeedback.light();
          refreshOpportunities();
          // setShowFoxModal(true);
        }}
        disabled={isLoading || refreshCooldown > 0}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4C7.58 4 4.01 7.58 4.01 12C4.01 16.42 7.58 20 12 20C15.73 20 18.84 17.45 19.73 14H17.65C16.83 16.33 14.61 18 12 18C8.69 18 6 15.31 6 12C6 8.69 8.69 6 12 6C13.66 6 15.14 6.69 16.22 7.78L13 11H20V4L17.65 6.35Z" fill="currentColor" />
        </svg>
        {/* <img style={{ position: 'absolute', opacity: 0.2, pointerEvents: 'none' }} src={wolfLogo} alt="Fox" /> */}
      </button>
    </div>
  ), [refreshCooldown, isLoading, refreshOpportunities, t]);

  const renderOpportunityList = () => (
    <>
      {renderBalanceCard()}

      <h2 className="section-title">{t('chooseTradeModeTitle')}</h2>

      <div className="trade-mode-buttons">
        <button
          className={`mode-button ${tradeMode === 'manual' ? 'active' : ''}`}
          onClick={() => handleModeSelect('manual')}
        >
          {t('manualMode')}
        </button>
        <button
          className={`mode-button ${tradeMode === 'automatic' ? 'active' : ''}`}
          onClick={() => handleModeSelect('automatic')}
        >
          {t('automaticMode')}
        </button>
      </div>

      {renderRefreshSection()}

      {isLoading ? (
        <div className="loading-indicator">{t('loading')}</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : opportunities.length === 0 ? (
        <div className="no-opportunities">
          {t('noArbitrageOpportunitiesMessage')}
        </div>
      ) : (
        <div className="opportunities-list">
          {opportunities.map(opportunity => (
            <div key={opportunity.id} className="opportunity-card" onClick={() => opportunity.canSwap ? handleOpportunitySelect(opportunity) : null}>
              <div className="opportunity-header">
                <div className="opportunity-pair">{opportunity.pair}</div>
                <div className="opportunity-status" style={{ color: opportunity.canSwap ? 'rgba(255, 255, 255, 0.7)' : '#ff7171' }}>
                  {opportunity.canSwap ? t('signalAvailable') : t('signalNotActive')}
                </div>
              </div>

              <div className="opportunity-divider"></div>

              <div className="opportunity-content">
                <div className="opportunity-column">
                  <div className="column-title">{t('buyPrices')}</div>
                  {Object.entries(opportunity.prices).map(([exchange, data]) => (
                    <div
                      key={exchange}
                      className="price-row"
                      style={{ opacity: data.isPrimary ? 1 : 0.3 }}
                    >
                      <div className="exchange-name">{exchange}:</div>
                      <div className="exchange-price">
                        ${typeof data.price === 'number' ? data.price.toFixed(2) : '0.00'}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="opportunity-column">
                  <div className="column-title">{t('bestConditionsTitle')}</div>
                  <div className="best-condition">
                    <div>{t('buyOnLabel')} {opportunity.buyExchange}:</div>
                    <div>${typeof opportunity.buyPrice === 'number' ? opportunity.buyPrice.toFixed(2) : '0.00'}</div>
                  </div>
                  <div className="best-condition">
                    <div>{t('sellOnLabel')} {opportunity.sellExchange}:</div>
                    <div>${typeof opportunity.sellPrice === 'number' ? opportunity.sellPrice.toFixed(2) : '0.00'}</div>
                  </div>
                  <div className="spread-row">
                    <div>{t('spreadLabel')}:</div>
                    <div className="spread-value">{typeof opportunity.spread === 'number' ? opportunity.spread.toFixed(2) : '0.00'}%</div>
                  </div>
                </div>
              </div>

              <div className="button-timestamp-row">
                <button
                  className={`start-trade-button ${!opportunity.canSwap || balance <= 0 ? 'disabled' : ''}`}
                  disabled={!opportunity.canSwap || balance <= 0}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent card click handler from firing
                    if (!opportunity.canSwap) {
                      hapticFeedback.error();
                      return;
                    }

                    if (balance <= 0) {
                      hapticFeedback.error();
                      setError(t('insufficientBalanceMessage'));
                      return;
                    }

                    hapticFeedback.medium();
                    handleOpportunitySelect(opportunity);
                  }}
                >
                  {t('startButton')}
                </button>
                <div className="opportunity-timestamp">{opportunity.date} в {opportunity.timestamp}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );

  const renderTradeForm = () => (
    <>
      <h1 className="page-title">{t('trade')}</h1>

      <div className="trade-details-card">
        <div className="trade-pair">{selectedOpportunity.pair}</div>
        <div className="trade-exchanges">
          <div>{t('buyOnLabel')} {selectedOpportunity.buyExchange}: ${typeof selectedOpportunity.buyPrice === 'number' ? selectedOpportunity.buyPrice.toFixed(2) : '0.00'}</div>
          <div>{t('sellOnLabel')} {selectedOpportunity.sellExchange}: ${typeof selectedOpportunity.sellPrice === 'number' ? selectedOpportunity.sellPrice.toFixed(2) : '0.00'}</div>
        </div>

        <div className="trade-info-row">
          <div className="trade-info-label">{t('availableInDeposit')}</div>
          <div className="trade-info-value">${balance.toFixed(2)}</div>
        </div>

        <div className="trade-info-row">
          <div className="trade-info-label">{t('tradesRemaining')}</div>
          <div className="trade-info-value">{availableTrades}</div>
        </div>

        <div className="trade-info-row">
          <div className="trade-info-label">{t('minimumAmount')}</div>
          <div className="trade-info-value">${minPerDeal}</div>
        </div>

        <div className="trade-info-row">
          <div className="trade-info-label">{t('maximumAmount')}</div>
          <div className="trade-info-value">${maxPerDeal}</div>
        </div>
      </div>

      <div className="amount-input">
        <input
          type="number"
          value={amount}
          onChange={handleAmountChange}
          placeholder={t('enterAmount')}
        />
      </div>

      {error && <div className="error-message">{error}</div>}

      {parseFloat(amount) > balance && !error && (
        <div className="error-message">{t('insufficientBalanceMessage')}</div>
      )}

      {parseFloat(amount) < minPerDeal && parseFloat(amount) > 0 && !error && (
        <div className="error-message">{t('minimumDealAmount')} ${minPerDeal}</div>
      )}

      {parseFloat(amount) > maxPerDeal && maxPerDeal > 0 && !error && (
        <div className="error-message">{t('maximumDealAmount')} ${maxPerDeal}</div>
      )}

      <button
        className="next-button"
        disabled={
          !amount ||
          parseFloat(amount) <= 0 ||
          parseFloat(amount) > balance ||
          parseFloat(amount) < minPerDeal ||
          (maxPerDeal > 0 && parseFloat(amount) > maxPerDeal) ||
          isLoading
        }
        onClick={() => {
          if (!(!amount || parseFloat(amount) <= 0 || parseFloat(amount) > balance || parseFloat(amount) < minPerDeal || (maxPerDeal > 0 && parseFloat(amount) > maxPerDeal) || isLoading)) {
            hapticFeedback.medium();
          }
          handleNext();
        }}
      >
        {isLoading ? t('loading') : t('next')}
      </button>
    </>
  );

  const renderTradeExecution = () => (
    <>
      <h1 className="page-title">{t('launchArbitrageTrade')}</h1>

      <div className="execution-card">
        <div className="execution-section">
          <div className="execution-section-title">{t('tradeParameters')}</div>
          <div className="execution-row">
            <div className="execution-label">{t('pair')}</div>
            <div className="execution-value">{selectedOpportunity.pair}</div>
          </div>
          <div className="execution-row">
            <div className="execution-label">{t('amount')}</div>
            <div className="execution-value">${amount}</div>
          </div>
          <div className="execution-row">
            <div className="execution-label">{t('volume')}</div>
            <div className="execution-value">{(parseFloat(amount) / (parseFloat(selectedOpportunity.buyPrice) || 1)).toFixed(8)}</div>
          </div>
        </div>

        <div className="execution-section">
          <div className="execution-section-title">{t('executionConditions')}</div>
          <div className="execution-row">
            <div className="execution-label">{t('buyOnLabel')}</div>
            <div className="execution-value">{selectedOpportunity.buyExchange}</div>
          </div>
          <div className="execution-row">
            <div className="execution-label">{t('sellOnLabel')}</div>
            <div className="execution-value">{selectedOpportunity.sellExchange}</div>
          </div>
          <div className="execution-row">
            <div className="execution-label">{t('spreadLabel')}</div>
            <div className="execution-value">{typeof selectedOpportunity.spread === 'number' ? selectedOpportunity.spread.toFixed(2) : '0.00'}%</div>
          </div>
          <div className="execution-row">
            <div className="execution-label">{t('expectedProfit')}</div>
            <div className="execution-value">${(parseFloat(amount) * (parseFloat(selectedOpportunity.spread) || 0) / 100).toFixed(2)}</div>
          </div>
        </div>
      </div>

      <div className="execution-progress-section">
        <div className="execution-status">
          {executionSteps[executionStep]?.text}
          {executionSteps[executionStep]?.exchange && selectedOpportunity[executionSteps[executionStep].exchange] &&
            ` (${selectedOpportunity[executionSteps[executionStep].exchange]})`
          }
        </div>

        <div className="progress-bar-container">
          <div className="progress-bar">
            <div
              className="progress-bar-fill"
              style={{ width: `${executionProgress}%` }}
            ></div>
          </div>
          <div className="progress-percentage">{Math.round(executionProgress)}%</div>
        </div>

        <div className="execution-steps">
          {executionSteps.map((step, index) => (
            <div
              key={index}
              className={`execution-step ${index < executionStep ? 'completed' : index === executionStep ? 'active' : 'pending'}`}
            >
              <div className="step-indicator">
                {index < executionStep ? '✓' : index === executionStep ? '⟳' : '○'}
              </div>
              <div className="step-text">
                {step.text}
                {step.exchange && selectedOpportunity[step.exchange] &&
                  ` (${selectedOpportunity[step.exchange]})`
                }
              </div>
            </div>
          ))}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
    </>
  );

  const renderTradeCompleted = () => (
    <>
      <h1 className="trade-complete-title">{t('tradeCompletedTitle')}</h1>
      <p className="trade-complete-subtitle">
        {t('tradeCompletedMessage', { pair: tradeResult.pair })}
      </p>

      <div className="trade-result-card">
        <div className="trade-result-title">{t('tradeResults')}</div>

        <div className="trade-result-row">
          <div className="trade-result-label">{t('amount')}</div>
          <div className="trade-result-value">${tradeResult.amount.toFixed(2)}</div>
        </div>

        <div className="trade-result-row">
          <div className="trade-result-label">{t('profit')}</div>
          <div className="trade-result-value">${tradeResult.profit.toFixed(3)}</div>
        </div>

        <div className="trade-result-row">
          <div className="trade-result-label">{t('currentBalance')}</div>
          <div className="trade-result-value">${tradeResult.newBalance.toFixed(2)}</div>
        </div>

        <div className="trade-result-row">
          <div className="trade-result-label info-tooltip">
            {t('platformCommission')}
            <span className="info-icon">ⓘ</span>
            <div className="tooltip-content">
              {t('commissionTooltip')}
            </div>
          </div>
          <div className="trade-result-value">{tradeResult.commissionPercent}%</div>
        </div>

        <div className="trade-result-row">
          <div className="trade-result-label">{t('commissionAmount')}</div>
          <div className="trade-result-value">
            <FormattedNumber space={false} value={tradeResult.commissionAmount} suffix="USDT" decimalPlaces={4} />
          </div>
        </div>
      </div>

      <button className="done-button" onClick={() => {
        hapticFeedback.success();
        handleDone();
      }}>
        {t('done')}
      </button>
    </>
  );

  const renderAutomaticSetup = useCallback(() => {
    const availablePairs = ['BTCUSDT', 'ETHUSDT', 'SOLUSDT'];
    const totalRequired = parseFloat(autoAmount) || 0;

    // If auto-trading is active or completed, show execution screen
    if (autoTradeStatus && (autoTradeStatus.isActive || autoTradeStatus.isCompleted)) {
      return (
        <>
          {renderBalanceCard()}

          <h2 className="section-title">{t('chooseTradeModeTitle')}</h2>

          <div className="trade-mode-buttons">
            <button
              className={`mode-button ${tradeMode === 'manual' ? 'active' : ''}`}
              onClick={() => handleModeSelect('manual')}
            >
              {t('manualMode')}
            </button>
            <button
              className={`mode-button ${tradeMode === 'automatic' ? 'active' : ''}`}
              onClick={() => handleModeSelect('automatic')}
            >
              {t('automaticMode')}
            </button>
          </div>

          <div className="refresh-section">
            <div className="refresh-label">
              {t('refreshStatus')}
              {statusRefreshCooldown > 0 && (
                <span className="refresh-cooldown"> ({statusRefreshCooldown}с)</span>
              )}
            </div>
            <button
              className="refresh-button"
              onClick={loadAutoTradeStatus}
              disabled={isLoadingStatus || statusRefreshCooldown > 0}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4C7.58 4 4.01 7.58 4.01 12C4.01 16.42 7.58 20 12 20C15.73 20 18.84 17.45 19.73 14H17.65C16.83 16.33 14.61 18 12 18C8.69 18 6 15.31 6 12C6 8.69 8.69 6 12 6C13.66 6 15.14 6.69 16.22 7.78L13 11H20V4L17.65 6.35Z" fill="currentColor" />
              </svg>
            </button>
          </div>

          {isLoadingStatus ? (
            <div className="execution-card skeleton-card">
              <div className="execution-section">
                <div className="execution-section-title skeleton-text skeleton-title"></div>
                <div className="execution-row">
                  <div className="execution-label skeleton-text skeleton-label"></div>
                  <div className="execution-value skeleton-text skeleton-value"></div>
                </div>
                <div className="execution-row">
                  <div className="execution-label skeleton-text skeleton-label"></div>
                  <div className="execution-value skeleton-text skeleton-value"></div>
                </div>
                <div className="execution-row">
                  <div className="execution-label skeleton-text skeleton-label"></div>
                  <div className="execution-value skeleton-text skeleton-value"></div>
                </div>
                <div className="execution-row">
                  <div className="execution-label skeleton-text skeleton-label"></div>
                  <div className="execution-value skeleton-text skeleton-value"></div>
                </div>
              </div>

              <div className="execution-section">
                <div className="execution-section-title skeleton-text skeleton-title"></div>
                <div className="execution-row">
                  <div className="execution-label skeleton-text skeleton-label"></div>
                  <div className="execution-value skeleton-text skeleton-value"></div>
                </div>
                <div className="execution-row">
                  <div className="execution-label skeleton-text skeleton-label"></div>
                  <div className="execution-value skeleton-text skeleton-value"></div>
                </div>
                <div className="execution-row">
                  <div className="execution-label skeleton-text skeleton-label"></div>
                  <div className="execution-value skeleton-text skeleton-value"></div>
                </div>
              </div>
            </div>
          ) : (
            <div className="execution-card">
              <div className="execution-section">
                <div className="execution-section-title">{t('autoTradingParameters')}</div>
                <div className="execution-row">
                  <div className="execution-label">{t('currencyPairsLabel')}</div>
                    <div className="execution-value rtsbr">{autoTradeStatus.coinPairs.map(coin =>
                    <div className='sbr' key={coin.name}>{coin.name} <span className={`execution-value-s ${coin.isSpreed ? "active" : "inactive"}`} /></div>
                  )}</div>
                </div>
                <div className="execution-row">
                  <div className="execution-label">{t('tradesProgress')}</div>
                  <div className="execution-value">{autoTradeStatus.completedDeals} / {autoTradeStatus.totalDeals}</div>
                </div>
                <div className="execution-row">
                  <div className="execution-label">{t('automationAmount')}</div>
                  <div className="execution-value">${autoTradeStatus.amountPerDeal}</div>
                </div>
                <div className="execution-row">
                  <div className="execution-label">{t('targetProfit')}</div>
                  <div className="execution-value">${(autoTradeStatus.amountPerDeal * autoTradeStatus.totalDeals * ((autoTradeStatus.maxSpread + autoTradeStatus.minSpread) / 3) / 100).toFixed(2)}</div>
                </div>
              </div>

              <div className="execution-section">
                <div className="execution-section-title">{t('executionConditions')}</div>
                <div className="execution-row">
                  <div className="execution-label">{t('spreadRangeLabel')}</div>
                  <div className="execution-value">{autoTradeStatus.minSpread}% - {autoTradeStatus.maxSpread}%</div>
                </div>
                <div className="execution-row">
                  <div className="execution-label">{t('tradesRemaining')}</div>
                  <div className="execution-value">{autoTradeStatus.remainingDeals}</div>
                </div>
                <div className="execution-row">
                  <div className="execution-label">{t('status')}</div>
                  <div className="execution-value">{autoTradeStatus.isCompleted ? t('statusCompleted') : t('statusActive')}</div>
                </div>
              </div>

              {autoTradeStatus.totalProfit > 0 && (
                <div className="execution-section">
                  <div className="execution-section-title">{t('results')}</div>
                  <div className="execution-row">
                    <div className="execution-label">{t('totalProfit')}</div>
                    <div className="execution-value">${autoTradeStatus.totalProfit.toFixed(2)}</div>
                  </div>
                  <div className="execution-row">
                    <div className="execution-label">{t('averageProfitPerTrade')}</div>
                    <div className="execution-value">${autoTradeStatus.completedDeals > 0 ? (autoTradeStatus.totalProfit / autoTradeStatus.completedDeals).toFixed(2) : '0.00'}</div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="execution-status">
            {autoTradeStatus.isCompleted
              ? t('autoTradingCompleted', { completed: autoTradeStatus.completedDeals, total: autoTradeStatus.totalDeals })
              : t('autoTradingActive')
            }
          </div>

          <div className="auto-trade-actions">
            {autoTradeStatus.isCompleted ? (
              <button
                className="start-auto-button"
                onClick={handleNewSession}
                disabled={isLoading}
              >
                {isLoading ? t('startingAutoTrading') : t('startNewSessionButton')}
              </button>
            ) : (
              <button
                className="stop-auto-button"
                onClick={handleStopAutoTrade}
                disabled={isLoading}
              >
                {isLoading ? t('stoppingAutoTrading') : t('stopAutoTradingButton')}
              </button>
            )}
          </div>

          {error && <div className="error-message">{error}</div>}
        </>
      );
    }

    // If auto-trading is not active, show setup form
    return (
      <>
        {renderBalanceCard()}

        <h2 className="section-title">{t('chooseTradeModeTitle')}</h2>

        <div className="trade-mode-buttons">
          <button
            className={`mode-button ${tradeMode === 'manual' ? 'active' : ''}`}
            onClick={() => handleModeSelect('manual')}
          >
            {t('manualMode')}
          </button>
          <button
            className={`mode-button ${tradeMode === 'automatic' ? 'active' : ''}`}
            onClick={() => handleModeSelect('automatic')}
          >
            {t('automaticMode')}
          </button>
        </div>

        <div className="auto-trade-form-container">
          <h2 className="auto-form-title">{t('autoTradingSettingsTitle')}</h2>

          <form className="auto-trade-form" onSubmit={handleAutoTradeSetup}>
            {/* Currency pairs selection */}
            <div className="form-group">
              <label className="form-label">{t('currencyPairsLabel')}</label>
              <div className="currency-pairs-grid">
                {availablePairs.map(pair => (
                  <button
                    key={pair}
                    type="button"
                    className={`pair-button ${selectedPairs.includes(pair) ? 'active' : ''}`}
                    onClick={() => handlePairToggle(pair)}
                  >
                    {pair}
                    {selectedPairs.includes(pair) && (
                      <span className="checkmark">✓</span>
                    )}
                  </button>
                ))}
              </div>
              <div className="selected-pairs-info">
                {t('selectedPairsCount', { count: selectedPairs.length })}
              </div>
            </div>

            <div className="form-divider"></div>

            {/* Number of deals */}
            <div className="form-group">
              <label className="form-label">{t('numberOfTradesLabel')}</label>
              <input
                type="number"
                min="1"
                max="100"
                className="form-input"
                placeholder={t('enterNumberOfTrades')}
                value={tradeCount}
                onChange={(e) => {
                  setTradeCount(e.target.value);
                  setError(null); // Clear error when user changes input
                }}
                required
              />
            </div>

            <div className="form-divider"></div>

            {/* Spread range */}
            <div className="form-group">
              <label className="form-label">{t('spreadRangeLabel')}</label>
              <div className="spread-inputs">
                <div className="spread-input-group">
                  <label className="spread-label">{t('fromLabel')}</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.05"
                    max="5.0"
                    className="form-input spread-input"
                    placeholder="0.50"
                    value={minSpread}
                    onChange={(e) => {
                      setMinSpread(e.target.value);
                      setError(null); // Clear error when user changes input
                    }}
                    required
                  />
                </div>
                <div className="spread-input-group">
                  <label className="spread-label">{t('toLabel')}</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.05"
                    max="10.0"
                    className="form-input spread-input"
                    placeholder="2.00"
                    value={maxSpread}
                    onChange={(e) => {
                      setMaxSpread(e.target.value);
                      setError(null); // Clear error when user changes input
                    }}
                    required
                  />
                </div>
              </div>
              <div className="spread-hint">
                {t('spreadRangeDescription')}
              </div>
            </div>

            <div className="form-divider"></div>

            {/* Amount per deal */}
            <div className="form-group">
              <label className="form-label">{t('amountPerTradeLabel')}</label>
              <input
                type="number"
                min="10"
                step="0.01"
                className="form-input"
                placeholder={t('enterAmount')}
                value={autoAmount}
                onChange={(e) => {
                  setAutoAmount(e.target.value);
                  setError(null); // Clear error when user changes input
                }}
                required
              />
              {totalRequired > 0 && (
                <div className="total-required">
                  {t('amountPerTradeValue', { amount: totalRequired.toFixed(2) })}
                  {totalRequired > balance && (
                    <span className="insufficient-balance"> {t('insufficientFundsNote')}</span>
                  )}
                </div>
              )}
            </div>

            <button
              type="submit"
              className="start-auto-button"
              disabled={
                !selectedPairs.length ||
                !tradeCount ||
                !minSpread ||
                !maxSpread ||
                !autoAmount ||
                totalRequired > user?.lincenseData?.maxDealAmount ||
                parseFloat(autoAmount) > balance ||
                parseFloat(minSpread) >= parseFloat(maxSpread) ||
                isLoading
              }
            >
              {isLoading ? t('startingAutoTrading') : t('startAutoTradingButton')}
            </button>

            <button
              type="button"
              className="refresh-status-button"
              onClick={loadAutoTradeStatus}
              disabled={isLoadingStatus || statusRefreshCooldown > 0}
              style={{ marginTop: '10px' }}
            >
              {isLoadingStatus ? t('checkingStatus') : t('checkCurrentSessionStatus')}
            </button>

            {/* Error display */}
            {error && (
              <div className="error-message">{error}</div>
            )}

            {/* Validation messages */}
            {parseFloat(minSpread) >= parseFloat(maxSpread) && minSpread && maxSpread && (
              <div className="error-message">{t('minSpreadLowerThanMax')}</div>
            )}

            {totalRequired > balance && totalRequired > 0 && (
              <div className="error-message">
                {t('insufficientBalanceDetailed', { required: totalRequired.toFixed(2), available: balance.toFixed(2) })}
              </div>
            )}
            {totalRequired > user?.lincenseData?.maxDealAmount && (
              <div className="error-message">
                {t('amountExceedsMaxDeal', { maxAmount: user?.lincenseData?.maxDealAmount })}
              </div>
            )}
          </form>
        </div>
      </>
    );
  }, [
    autoAmount,
    tradeCount,
    autoTradeStatus,
    renderBalanceCard,
    tradeMode,
    handleModeSelect,
    statusRefreshCooldown,
    loadAutoTradeStatus,
    isLoadingStatus,
    isLoading,
    handleNewSession,
    handleStopAutoTrade,
    error,
    selectedPairs,
    minSpread,
    maxSpread,
    balance,
    handleAutoTradeSetup,
    handlePairToggle
  ]);

  const renderCurrentStage = () => {
    switch (tradeStage) {
      case TRADE_STAGES.SELECT_MODE:
        return renderModeSelection();
      case TRADE_STAGES.SELECT_OPPORTUNITY:
        return renderOpportunityList();
      case TRADE_STAGES.AUTOMATIC_SETUP:
        return renderAutomaticSetup();
      case TRADE_STAGES.ENTER_AMOUNT:
        return renderTradeForm();
      case TRADE_STAGES.EXECUTION:
        return renderTradeExecution();
      case TRADE_STAGES.COMPLETED:
        return renderTradeCompleted();
      default:
        return renderModeSelection();
    }
  };

  const renderFoxModal = () => {
    if (!showFoxModal) return null;

    return (
      <div className="fox-modal-overlay" onClick={() => setShowFoxModal(false)}>
        <div className="fox-modal" onClick={(e) => e.stopPropagation()}>
          <button
            className="fox-modal-close"
            onClick={() => setShowFoxModal(false)}
          >
            ×
          </button>

          <div className="fox-modal-content">
            <div className="fox-logo">
              <img src={config.logo} alt="Fox Logo" />
            </div>

            <h2 className="fox-modal-title">{t('getFoxTokens')}</h2>

            <p className="fox-modal-description">
              {t('getFoxTokensDescription')}
            </p>

            <SwipeToGetFoxButton onGetFox={handleGetFoxTokens} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="arbitrage-page">
      {renderCurrentStage()}
      {renderFoxModal()}
    </div>
  );
};

export default Arbitrage; 